import { useContext, useEffect } from 'react'
import { Link, Link as RouterLink, useLocation, useNavigate } from 'react-router-dom'
// material
import { Box, Divider, Drawer, Stack } from '@mui/material'
import { alpha } from '@mui/material/styles'
// hooks
import useCollapseDrawer from '../../hooks/useCollapseDrawer'

// components
import { ReactComponent as MyGuarantee } from '../../assets/icons/myguarantee.svg'

import NavSection, { Footer } from '../../components/NavSection'
//
import { Icon } from '@iconify/react'
import { styled } from '@mui/material/styles'
import Cookies from 'js-cookie'
import AccountExpanded from 'layouts/common/AccountExpanded'
import { AccountSkeleton, NavItemSkeleton, ButtonSkeleton } from '../../components/SkeletonVariants'
import { useSelector } from 'react-redux'
import { RootState } from 'redux/store'
import FadeMenu from './CreateButtonNavigation'
import { clearPersistedFormData } from 'utils/localStorage'
import { immediateReduxReset, waitForReduxReset } from 'utils/immediateLogout'
import sidebarConfig, { adminSettings, settingsConfig, termsAndConditions } from './SidebarConfig'
import { RootStyle } from './styles/index.b'
// ----------------------------------------------------------------------
import { GlobalInfoContext, initialState } from 'contexts/GlobalStateContext'

import { useAuth } from 'hooks/useAuth'
import { useSocketConnection  } from 'hooks/useSocketConnection';
import { useSnackbar } from 'notistack'
//import { useNavigate } from 'react-router'
import { UIContractState } from '@types'
import { clearAllReduxCaches } from 'pages/dashboard/utils'
import { dispatch } from 'redux/store'
import { PRIMARY, SECONDARY } from 'theme/palette'
import CreatePaperGuarantee from './CreatePaperGuaranteeNavigation'

const DRAWER_WIDTH = 300
const COLLAPSE_WIDTH = 110

type DashboardSidebarProps = {
  isOpenSidebar: boolean
  onCloseSidebar: VoidFunction
  noOfTasks: number
  noOfNotifications: number
  noOfOrgTasks: number
}

const Container = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'flex-end',
  height: '100%',
  color: '#000000',
  /*@ts-ignore*/
  backgroundColor: window?.brand === 'nab' ? '#000000' : SECONDARY.main
})

const NavItem = styled('div')`
  padding: 10px 0 10px 32px;
  cursor: pointer;

  & a {
    display: flex;
    color: #000000;
    font-size: 14px;
    text-decoration: none;
    & svg {
      margin-right: 15px;
    }
  }
  &:hover {
    background-color: rgba(145, 158, 171, 0.08);
  }
`

const getLogo = (user) => {
  const role = user?.roles
  switch (role) {
    case 'admin':
      return <MyGuarantee />
    case 'issuer':
      return null
    case 'applicant':
      return null
    case 'beneficiary':
      return null
    default:
      return (
        <div style={{ paddingLeft: '15px' }}>
          <MyGuarantee />
        </div>
      )
  }
}

export default function DashboardSidebar({
  isOpenSidebar,
  onCloseSidebar,
  noOfTasks,
  noOfNotifications,
  noOfOrgTasks
}: DashboardSidebarProps) {
  let bgColor = PRIMARY.newBg
  /*@ts-ignore*/
  if (window?.brand === 'nab') {
    bgColor = '#000000'
  }
  const { pathname } = useLocation()
  const { user, isLoading } = useSelector((state: RootState) => state.userReducer)
  const { businessUnitActionContexts: buacs, isLoading: buacsLoading } = useSelector(
    (state: RootState) => state.createGuaranteePayload
  )
  const { adminActions, isLoading: adminActionsLoading } = useSelector((state: RootState) => state.adminCreateActions)
  // Separate loading states for different sidebar sections
  const accountDataLoading = isLoading || !user?.nameLabelDisplay
  const navDataLoading = buacsLoading  // Show skeleton while fetching, hide when done (even if empty)
  const createButtonLoading = buacsLoading
  const adminDataLoading = adminActionsLoading
  const sideConfig = sidebarConfig(noOfTasks, noOfNotifications, noOfOrgTasks)
  const { isCollapse, collapseHover, onHoverEnter, onHoverLeave } = useCollapseDrawer()

  const navigate = useNavigate()
  const { logout } = useAuth()
  const { enqueueSnackbar } = useSnackbar()
  const { onSocketClosed } = useSocketConnection()

  const globalContext = useContext(GlobalInfoContext)

  const handleLogout = async () => {
    try {
      // Step 1: IMMEDIATE cleanup - synchronous, no delays
      Cookies.remove('token')
      globalContext.setReqInfo(initialState)
      
      // Step 2: Clear all persisted storage immediately
      clearPersistedFormData()
      
      // Step 3: Reset Redux state immediately to prevent stale data from showing
      immediateReduxReset()
      
      // Step 4: Clear all Redux caches
      clearAllReduxCaches(dispatch)

      // Step 5: Close socket connection
      onSocketClosed()

      // Step 6: Wait a brief moment for all cleanups to settle
      await waitForReduxReset(50)

      // Step 7: Perform actual logout with server
      await logout?.()

      // Step 8: Navigate to login
      navigate('/auth/login')
      
      // Step 9: Clear browser history to prevent back button issues
      window.history.replaceState(null, '', '/')
    } catch (error) {
      console.error('Logout error:', error)
      enqueueSnackbar('Unable to logout', { variant: 'error' })
      // Force redirect even if error occurs
      navigate('/auth/login')
      window.location.href = '/auth/login'
    }
  }

  useEffect(() => {
    if (isOpenSidebar) {
      onCloseSidebar()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname])
  // Ensure `buacs` is loaded before rendering `FadeMenu`
  const shouldShowFadeMenu = canCreateByType(buacs, 'TriContract')
  const isAdmin = adminActions?.length > 0
  const renderContent = (
    <Box
      sx={{
        height: 1,
        backgroundColor: bgColor,
        color: '#000000',
        '& a': {
          fontSize: '14px',
          fontWeight: 'bold',
          color: '#000000',
          textDecoration: 'none'
        },
        overflow: 'hidden'
      }}
      key="scroll-bar"
    >
      <div>
        <Stack
          spacing={3}
          sx={{
            px: 2.5,
            pt: 3,
            pb: 2,
            ...(isCollapse && {
              alignItems: 'center'
            })
          }}
        >
          <Stack key="stack-nav" direction="row" alignItems="center" justifyContent="space-between">
            <Box
              key="stack-box"
              component={RouterLink}
              to="/dashboard/app"
              sx={{ display: 'inline-flex' }}
              aria-labelledby="arc-gx-logo"
            >
              {getLogo(user)}
            </Box>
          </Stack>
        </Stack>

        {accountDataLoading ? <AccountSkeleton /> : <AccountExpanded user={user} />}
        {navDataLoading ? <NavItemSkeleton count={6} /> : (
          <NavSection
            key="nav-section-desktop"
            navConfig={sideConfig}
            isShow={true}
            className="sidebar"
          />
        )}
        {createButtonLoading ? (
          <ButtonSkeleton width="260px" height={44} />
        ) : shouldShowFadeMenu ? (
          <FadeMenu />
        ) : null}
        {buacs.length > 0 && canCreateByType(buacs, 'TriContractPaperRecord') && (
          <CreatePaperGuarantee />
        )}
        {adminDataLoading ? (
          <NavItemSkeleton count={1} />
        ) : (
          isAdmin && (
            <NavItem>
              <Link to="/dashboard/admin" data-testid="admin">
                <Icon icon="mdi:settings" width={24} height={24} color="#000" />
                Admin
              </Link>
            </NavItem>
          )
        )}
        <Divider sx={{ mt: 3 }} />
        {navDataLoading ? (
          <NavItemSkeleton count={4} />
        ) : (
          <NavSection
            key="nav-section-desktop-settings"
            navConfig={settingsConfig}
            isShow={true}
            className="setting-sidebar"
          />
        )}
        {navDataLoading ? (
          <NavItemSkeleton count={1} />
        ) : (
          <NavItem>
            <Link to="#" onClick={handleLogout} data-testid="logout">
              <Icon icon="mdi-light:logout" width={24} height={24} color="#000000" />
              Log Out
            </Link>
          </NavItem>
        )}
      </div>

      <Stack
        key="stack-nav"
        direction="column"
        justifyContent="space-between"
        sx={{ margin: '60px 0 60px 26px' }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            margin: '5px'
          }}
        >
          <Icon
            icon="fluent:chat-help-24-regular"
            width={24}
            height={24}
            style={{ marginRight: '10px' }}
            color="#000000"
          />
          <a href="mailto:support@myguarantee.com.au">support@myguarantee.com.au</a>
        </Box>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            margin: '5px'
          }}
        >
          <Icon
            icon="solar:phone-outline"
            width={24}
            height={24}
            style={{ marginRight: '10px' }}
            color="#000000"
          />
          <a href="tel:1300 156 703">1300 156 703</a>
        </Box>
      </Stack>

      <Container>
        <div>
          <Footer items={termsAndConditions} />
        </div>
      </Container>
    </Box>
  )

  return (
    <RootStyle
      sx={{
        width: {
          lg: DRAWER_WIDTH,
          zIndex: 1
        }
      }}
    >
      <div>
        <Drawer
          open
          variant="persistent"
          onMouseEnter={onHoverEnter}
          onMouseLeave={onHoverLeave}
          PaperProps={{
            sx: {
              width: DRAWER_WIDTH,
              bgcolor: 'background.default',
              ...(isCollapse && {
                width: COLLAPSE_WIDTH
              }),
              ...(collapseHover && {
                borderRight: 0,
                backdropFilter: 'blur(6px)',
                WebkitBackdropFilter: 'blur(6px)',
                bgcolor: (theme) => alpha(theme.palette.background.default, 0.88)
              })
            }
          }}
        >
          {renderContent}
        </Drawer>
      </div>
    </RootStyle>
  )
}

const canCreateByType = (buacs: Array<UIContractState>, type: string) =>
  buacs.length > 0 && buacs.some((buac) => buac?.contractType === type)
